import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type Topic = {
  key: string;
  to: string;
  label: string;
  icon: ReactNode;
  /** What the photograph behind this tile should be, in English: read by
   * screen readers and counted in docs/PHASE2-REDESIGN-REPORT.md. Matches the
   * `[PHOTO TO SUPPLY]` convention used by `PhotoPanel` and `RoomPhoto`, kept
   * to a dashed placeholder box rather than a repeated on screen sentence,
   * since nine of them side by side would be its own wall of text. */
  photoTodo: string;
};

/**
 * The "Browse" photo grid of topics (docs/GUEST-REDESIGN.md Section 5, item 4):
 * two across, each a picture, an icon and a name. There are no photographs yet
 * (content/guest/PLACEHOLDERS.md, item 30, and the ones this phase adds), so
 * every tile is a dashed placeholder box rather than an empty or a fake one.
 *
 * "As many as fit in 150-point columns" (Section 3), the same rule as the
 * home's tile grid, so at 200 percent zoom this falls to one column instead
 * of squeezing two narrow ones (a fixed `grid-cols-2` did exactly that: at a
 * 195 px zoomed viewport each column was under 76 px wide).
 *
 * The same rule on a wide desktop browser fits three or more, not the two
 * across the doc calls for, so the list itself carries a max-w capping it at
 * two (see the home tile grid's own note on the same fix).
 */
export function TopicGrid({ topics }: { topics: Topic[] }) {
  return (
    <ul className="grid max-w-[25rem] grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-3">
      {topics.map((topic) => (
        <li key={topic.key}>
          <Link
            to={topic.to}
            className="flex flex-col overflow-hidden rounded-xl border border-line bg-surface min-h-tap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <div className="flex h-20 items-center justify-center border-b border-dashed border-line bg-surfaceDeep text-accent">
              <span className="h-7 w-7" aria-hidden="true">
                {topic.icon}
              </span>
              <span className="sr-only">{`[PHOTO TO SUPPLY: ${topic.photoTodo}]`}</span>
            </div>
            <span className="flex min-h-tap items-center px-3 py-2 text-[0.95em] font-medium leading-tight text-ink">
              {topic.label}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
